import { accessToken } from './auth';
import type { RuntimeConfig } from './config';

/**
 * The staff API client.
 *
 * Deliberately its own file rather than an import from web/: the admin panel
 * talks to a different audience and carries a role claim the family surface
 * does not, and sharing a client would make it one careless edit away from
 * sending a staff token to a family route or vice versa.
 *
 * Every route the portal knows about lives here, in one place, so the answer to
 * "what can this console reach" is one file rather than a grep. The types below
 * are written against what `backend/lambda/api/admin.py`,
 * `admin_pipeline.py` and `azure/functions/function_app.py` actually deploy —
 * read, not assumed. Where a field is optional it is because the handler can
 * genuinely omit it, and the comment says why.
 *
 * THE PATHS BELOW ALL START `/admin/...` AND THAT IS STILL CORRECT. On Azure the
 * staff surface sits under /control, which is carried in `apiEndpoint` — see
 * config.ts. /control wraps rather than replaces, so the handlers' own
 * `endswith('/admin/...')` matching is untouched and so is every line here.
 */

let config: RuntimeConfig | null = null;

export function configureApi(next: RuntimeConfig): void {
  config = next;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message: string,
  ) {
    super(message);
  }
}

async function authHeader(): Promise<string> {
  // An ACCESS token, and auth.ts explains at length why that is not the same
  // decision the Cognito version made.
  const token = await accessToken();
  if (!token) throw new ApiError(401, null, 'not signed in');
  return `Bearer ${token}`;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!config) throw new Error('configureApi() was never called');
  const res = await fetch(config.apiEndpoint + path, {
    ...init,
    headers: {
      authorization: await authHeader(),
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  const raw = await res.text();
  let body: unknown = null;
  try {
    body = raw ? JSON.parse(raw) : null;
  } catch {
    // A gateway 502 is HTML. Surfacing "Unexpected token <" helps nobody.
    throw new ApiError(res.status, raw, `${res.status} — non-JSON response`);
  }
  if (!res.ok) {
    const shape = body as { error?: string; detail?: string } | null;
    // `detail` carries the sentence that explains the refusal — "verdicts
    // cannot be filtered by family… the cache records no familyId". Dropping it
    // and showing only `error` turns a self-explaining API into a riddle.
    const message = [shape?.error ?? `${res.status}`, shape?.detail]
      .filter(Boolean)
      .join(' — ');
    throw new ApiError(res.status, body, message);
  }
  return body as T;
}

/**
 * Is this a route the staff API does not have, as opposed to a route that has
 * it and refused?
 *
 * Two shapes now, because the port changed who answers an unknown path.
 *
 * The handlers answer an unmatched path they were still asked about with a
 * literal `{"error": "unknown admin route"}` / `"unknown pipeline route"`, and
 * every other 404 they produce says something specific — "unknown family",
 * "unknown asset", "no such execution".
 *
 * But on Azure a path that is not in function_app.py's route table never
 * reaches a handler at all: the Functions host 404s it itself, with an empty
 * body or an HTML one. `request()` above turns those into an ApiError whose
 * body is null or a raw string and whose whole message is "404". Without the
 * second clause this function returned false for the only 404 the deployment
 * can actually produce, every `missing=` prop in the portal became unreachable,
 * and a route that was never deployed read as an incident.
 */
export function isMissingRoute(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 404) return false;
  const body = err.body as { error?: unknown } | null;
  if (body?.error === 'unknown admin route' || body?.error === 'unknown pipeline route') {
    return true;
  }
  // No handler JSON at all: nothing below the host ever saw this request.
  return typeof body !== 'object' || body === null || typeof body.error !== 'string';
}

/**
 * The narrower half of `isMissingRoute`: the STAFF HANDLER answered, and said it
 * does not know this path.
 *
 * `isMissingRoute` folds two very different facts into one, which is right for
 * the twelve screens that only need "there is no such view here" — but wrong for
 * the one screen whose subject IS the API. The two facts:
 *
 *   this          — `{"error": "unknown admin route"}`, written by
 *                   `api/admin.py`'s own catch-all. To produce it the request
 *                   had to be routed by the Functions host, pass
 *                   `azure/functions/auth.py` (which answers 401 on a bad token
 *                   and 403 on a missing `staff` role BEFORE any handler runs),
 *                   and reach the module. So it is proof of almost everything
 *                   the Health screen wants to assert.
 *   the other one — a bodyless Kestrel 404. Nothing below the host saw the
 *                   request, so it says nothing at all about identity, and it
 *                   is the shape that genuinely might mean "not deployed".
 *
 * This matters today rather than hypothetically: `/control/admin/health` IS in
 * function_app.py's route table, and `api/admin.py` has no branch for it, so
 * the live deployment answers exactly the first shape.
 */
export function isHandlerUnknownRoute(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 404) return false;
  const body = err.body as { error?: unknown } | null;
  return body?.error === 'unknown admin route' || body?.error === 'unknown pipeline route';
}

/**
 * A route that is deployed, answered correctly, and will never work here.
 *
 * The third case, and the one this deployment needed. `Problem` is for a call
 * that failed and might not next time; `NotBuilt` is for a route that does not
 * exist. Neither fits "the pipeline view cannot be served by this lane" — that
 * is a permanent, correct, fully-explained refusal, and offering Try again for
 * it wastes an operator's time during an incident.
 *
 * 501 and 503 are one bucket on purpose. They mean different things to the
 * handler — 501 is "deployed but missing an env var" (FAMILY_USER_POOL_ID
 * unset, so /admin/users cannot read a directory), 503 is "this lane cannot
 * answer at all" (shared/workflow.py's DurableFunctions has no introspection,
 * so all four /admin/pipeline* routes refuse up front). They mean the same
 * thing to the person reading the screen, and both handlers return a full
 * sentence saying which — so the difference is carried in the server's own
 * words rather than in a status code the operator never sees.
 */
export function isUnavailable(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 501 || err.status === 503);
}

/**
 * Signed in, tenant is right, not in the `staff` role.
 *
 * azure/functions/auth.py answers this 403 rather than 401 deliberately: "a 401
 * would send the portal round the sign-in loop forever". Matched on the message
 * and not on the status alone, because the staff API has another 403 that means
 * something completely different — admin.py refuses to sign a URL for
 * family-scoped art, and treating that as "you are not staff" would throw the
 * whole console away over one asset.
 */
const NOT_STAFF = /^this account is not in the .+ role$/;

export function isNotStaff(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  const body = err.body as { error?: unknown } | null;
  return typeof body?.error === 'string' && NOT_STAFF.test(body.error);
}

/* ------------------------------------------------------------- list plumbing */

/**
 * A page from any of the paginated browsers.
 *
 * `_listing()` in admin.py returns `{<name>: rows, count, next, cap, scan?}`.
 * Three of those are load-bearing here:
 *
 *  - `next` is a real opaque cursor (a base64 LastEvaluatedKey), so paging walks
 *    the whole table rather than one arbitrary slice. It is also the ONLY
 *    honest signal that more rows exist — `count` is the page, not the total.
 *  - `cap` is the server's per-page maximum (200).
 *  - `scan.reason` is the handler saying "this had no index to read from, so
 *    'newest first' is true of this page and not of the table". That has to
 *    reach the screen: an operator sorting by date and paging will otherwise
 *    watch the dates jump and conclude the console is broken.
 */
export type Listing<T> = {
  rows: T[];
  /** Rows on THIS page. Never a total — there is no cheap total for a scan. */
  count: number;
  /** Opaque cursor for the next page; absent when this is the last one. */
  next?: string;
  cap?: number;
  scan?: { reason?: string; orderedWithinPage?: boolean };
};

/**
 * Pull the row array out of a list response.
 *
 * Tolerant of the key name, because a list endpoint names its own array
 * (`commissions`, `clips`, `verdicts`) and one helper should serve all of them.
 * NOT tolerant of finding no array at all: that throws, so an unexpected shape
 * surfaces as a stated error rather than as a permanently empty table that
 * looks like a calm "no results". The difference between those two is the whole
 * reason this portal has separate empty and error states.
 */
function listing<T>(body: unknown, key: string): Listing<T> {
  const meta = (body && typeof body === 'object' && !Array.isArray(body)
    ? (body as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const found = Array.isArray(body) ? body : meta[key];
  if (!Array.isArray(found)) {
    throw new ApiError(
      200,
      body,
      `the API answered without a "${key}" array — this portal does not know ` +
        'that response shape, so it is showing nothing rather than pretending ' +
        'the result was empty',
    );
  }
  const rows = found as T[];
  return {
    rows,
    count: typeof meta.count === 'number' ? meta.count : rows.length,
    ...(typeof meta.next === 'string' && meta.next ? { next: meta.next } : {}),
    ...(typeof meta.cap === 'number' ? { cap: meta.cap } : {}),
    ...(meta.scan && typeof meta.scan === 'object'
      ? { scan: meta.scan as Listing<T>['scan'] }
      : {}),
  };
}

/** Build `?a=1&b=2`, dropping anything empty so a blank filter is never sent
 *  as `?family=` — which several handlers would read as a real value. */
function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

function numberOr(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/* ------------------------------------------------------------------ families */

export type Family = {
  familyId: string;
  name: string;
  creditBalance: number | null;
  unlimitedCredits: boolean;
  createdAt?: string;
};

/** A principal bound to a family. Carries no email on purpose — showling-members
 *  stores none, and /admin/users reads the identity directory, which owns the
 *  address. On this deployment that read is unimplemented; see listUsers. */
export type Member = {
  principalId: string;
  familyId?: string;
  familyName?: string;
  role?: string;
  joinedVia?: string;
  createdAt?: string;
  migratedAt?: string;
};

export type LedgerEntry = {
  entryId: string;
  type: string;
  credits: number;
  at: string;
  reason?: string;
  actor?: string;
  waived?: boolean;
  wouldHaveCost?: number;
};

export type FamilyDetail = Family & {
  members?: Member[];
  ledger: LedgerEntry[];
  videos: Array<{
    videoId: string;
    title?: string;
    status?: string;
    createdAt?: string;
    stitched: boolean;
    reuse?: { segments?: number; reused?: number; generated?: number };
  }>;
  commissions: Array<{
    commissionId: string;
    status?: string;
    request?: string;
    createdAt?: string;
    failureReason?: string;
  }>;
};

export const listFamilies = () =>
  request<unknown>('/admin/families').then((body) => listing<Family>(body, 'families'));

export const getFamily = (id: string) =>
  request<FamilyDetail>(`/admin/families/${encodeURIComponent(id)}`);

export const adjustCredits = (id: string, credits: number, reason: string) =>
  request<{ creditBalance: number }>(
    `/admin/families/${encodeURIComponent(id)}/credits`,
    { method: 'POST', body: JSON.stringify({ credits, reason }) },
  );

export const setUnlimited = (id: string, unlimited: boolean) =>
  request<{ unlimitedCredits: boolean }>(
    `/admin/families/${encodeURIComponent(id)}/unlimited`,
    { method: 'POST', body: JSON.stringify({ unlimited }) },
  );

export const listMembers = (params: { family?: string; next?: string; limit?: number }) =>
  request<unknown>('/admin/members' + qs(params)).then((body) =>
    listing<Member>(body, 'members'),
  );

/* --------------------------------------------------------------------- users */

export type User = {
  sub: string;
  username: string;
  email?: string;
  emailVerified: boolean;
  status?: string;
  enabled?: boolean;
  createdAt?: string;
  /** null is a real answer, not a gap: a directory account that signed up and
   *  never opened the app has no member row and belongs to no family. */
  familyId?: string | null;
};

/**
 * The directory's own pagination token, not the table cursor the other lists
 * use. Same idea, different issuer — hence its own shape rather than `Listing`.
 *
 * 501 ON THIS DEPLOYMENT, and it is not a bug to chase. `/admin/users` reads a
 * user directory through shared/directory.py, whose only implementation is
 * Cognito; admin.py guards on FAMILY_USER_POOL_ID and refuses when it is unset,
 * which it always is on Azure. `isUnavailable` is what turns that into a
 * sentence on the Users screen instead of an empty table.
 */
export const listUsers = (params: { filter?: string; next?: string; limit?: number }) =>
  request<{ users: User[]; next?: string }>('/admin/users' + qs(params));

export const resetPassword = (sub: string) =>
  request<{ reset: string; detail: string }>(
    `/admin/users/${encodeURIComponent(sub)}/reset-password`,
    { method: 'POST' },
  );

/* --------------------------------------------------- commissions and the plan */

/**
 * One row of `showling-commissions`.
 *
 * `kind` is normalised by the handler: episodes store no `kind` at all and come
 * back as `"episode"`, peeks as `"preview"`. A peek is handed straight to a
 * background worker (shared/workers.py) and is NEVER an orchestrated run —
 * eight of them exist, so a screen that counted only runs would report the
 * pipeline idle while eight commissions sat in it.
 *
 * `orphaned` is the handler flagging a row with no `familyId` or no `createdAt`.
 * `veo-test-2` is real, live and exactly that: absent from every byFamily index
 * and therefore invisible to the family's own reads. Only a scan finds it.
 */
export type CommissionRow = {
  commissionId: string;
  familyId?: string | null;
  familyName?: string | null;
  kind?: string;
  status?: string;
  request?: string;
  characterId?: string;
  videoId?: string;
  creditsHeld?: number | null;
  /** Set on unlimited families: no credits moved, real provider money did. */
  creditsWaived?: number | null;
  createdAt?: string;
  approvedAt?: string;
  previewAt?: string;
  failureReason?: string;
  orphaned?: boolean;
};

export type ShotRow = {
  commissionId?: string;
  /** A zero-padded STRING sort key ("0000"), never a number. */
  shotIndex?: string;
  shotId?: string;
  status?: string;
  canonical?: string;
  description?: string;
  characterId?: string;
  characterName?: string;
  reuse?: string;
  qualityTier?: string;
  durationSeconds?: number;
  referenceKeys?: string[];
  tags?: Record<string, unknown>;
  plannedAt?: string;
};

export type CommissionDetail = CommissionRow & {
  shots?: ShotRow[];
  shotCount?: number;
  shotsTruncated?: boolean;
  shotStatuses?: Record<string, number>;
  /** The handler's own admission that shot status is a denominator with no
   *  numerator. Rendered verbatim rather than paraphrased. */
  shotStatusNote?: string;
  video?: {
    videoId?: string;
    title?: string;
    status?: string;
    segments?: number;
    stitched?: boolean;
    reuse?: Record<string, unknown>;
  } | null;
  character?: CharacterRow | null;
  ledger?: LedgerRow[];
};

export const listCommissions = (params: {
  status?: string;
  family?: string;
  kind?: string;
  claims?: string;
  next?: string;
  limit?: number;
}) =>
  request<unknown>('/admin/commissions' + qs(params)).then((body) =>
    listing<CommissionRow>(body, params.claims ? 'claims' : 'commissions'),
  );

export const getCommission = (id: string) =>
  request<CommissionDetail>(`/admin/commissions/${encodeURIComponent(id)}`);

export const listShots = (params: { commission: string; next?: string; limit?: number }) =>
  request<unknown>('/admin/shots' + qs(params)).then((body) =>
    listing<ShotRow>(body, 'shots'),
  );

/**
 * An `idem#` idempotency claim row, with the evidence that it is permanent.
 *
 * Both `showling-characters` and `showling-commissions` write a 24-hour `ttl`
 * and both have TTL DISABLED on the table, so nothing ever sweeps them — 30 of
 * 63 character rows are these. `ttlOverdueDays` is how long ago the row asked to
 * be deleted and was not.
 */
export type ClaimRow = {
  commissionId?: string;
  characterId?: string;
  familyId?: string;
  claims?: string;
  at?: string;
  ttl?: number | null;
  ttlAt?: string;
  ttlExpired?: boolean;
  ttlOverdueDays?: number;
};

export const listClaims = (
  entity: 'commissions' | 'characters',
  params: { next?: string; limit?: number },
) =>
  request<unknown>(`/admin/${entity}` + qs({ ...params, claims: '1' })).then((body) =>
    listing<ClaimRow>(body, 'claims'),
  );

/* ---------------------------------------------------------------- characters */

export type CharacterRow = {
  characterId: string;
  familyId?: string | null;
  familyName?: string | null;
  characterName?: string;
  canonical?: string;
  description?: string;
  status?: string;
  reuse?: string;
  starterSlug?: string;
  /** A key, never a URL. A character belongs to a household, so its portrait
   *  and its rejected candidates are that family's art and are not presigned
   *  from the control plane. */
  portraitKey?: string;
  portraitTier?: string;
  artCostUsd?: number;
  candidateCount?: number;
  chosenCandidate?: number;
  createdAt?: string;
  selectedAt?: string;
};

export type CharacterDetail = CharacterRow & {
  tags?: Record<string, unknown>;
  canonVersion?: string;
  candidates?: unknown[];
  candidatesAt?: string;
  mediaNote?: string;
};

export const listCharacters = (params: {
  family?: string;
  status?: string;
  reuse?: string;
  next?: string;
  limit?: number;
}) =>
  request<unknown>('/admin/characters' + qs(params)).then((body) =>
    listing<CharacterRow>(body, 'characters'),
  );

export const getCharacter = (id: string) =>
  request<CharacterDetail>(`/admin/characters/${encodeURIComponent(id)}`);

/* ------------------------------------------------------- assets and the pool */

export type Clip = {
  assetId: string;
  kind?: string;
  canonical?: string;
  reuse?: string;
  familyId?: string | null;
  familyName?: string | null;
  /** No owning family means the shared pool — which is also exactly the
   *  condition under which this asset is playable from the control plane. */
  shared?: boolean;
  storageKey?: string;
  provider?: string;
  costUsd?: number | null;
  durationSeconds?: number;
  commissionId?: string;
  shotId?: string;
  createdAt?: string;
  embedded: boolean;
};

/** `canonical -> assetId`, the index the reuse waterfall actually reads. */
export type Alias = {
  canonical?: string;
  assetId?: string;
  kind?: string;
  reuse?: string;
  familyId?: string | null;
  /** The phrase this alias used to be filed under. Its presence explains a
   *  duplicate; its absence is normal. */
  rekeyedFrom?: string;
  at?: string;
};

export type AssetDetail = Clip & {
  tags?: Record<string, unknown>;
  posterKey?: string;
  model?: string;
  source?: string;
  styleId?: string;
  tagSignature?: string;
  freshUntil?: string;
  /** Past this the veto stops offering it for reuse. It stays watchable. */
  stale?: boolean;
  bakedWith?: string;
  commission?: CommissionRow | null;
  /** `pointsHere: false` means SUPERSEDED — this asset's canonical phrase now
   *  serves a different asset, so it is stored, paid for and never served
   *  again. Invisible everywhere else in the system. */
  alias?: (Alias & { pointsHere?: boolean }) | null;
  url?: string | null;
  expiresInSeconds?: number | null;
  contentType?: string | null;
  /** The reason there is no url — a family-scoped refusal, or a missing file. */
  notPlayable?: string | null;
};

export const listClips = (params: {
  kind?: string;
  reuse?: string;
  family?: string;
  commission?: string;
  next?: string;
  limit?: number;
}) => request<unknown>('/admin/clips' + qs(params)).then((body) => listing<Clip>(body, 'clips'));

export const getAsset = (assetId: string) =>
  request<AssetDetail>(`/admin/assets/${encodeURIComponent(assetId)}`);

/** A short-lived presigned link to view or download one pooled asset. */
export const getClipUrl = (assetId: string) =>
  request<{ url: string; key: string; expiresInSeconds: number; contentType: string }>(
    `/admin/clips/${encodeURIComponent(assetId)}/url`,
  );

export const listAliases = (params: {
  q?: string;
  family?: string;
  kind?: string;
  reuse?: string;
  next?: string;
  limit?: number;
}) =>
  request<unknown>('/admin/aliases' + qs(params)).then((body) =>
    listing<Alias>(body, 'aliases'),
  );

/* -------------------------------------------------------------------- videos */

export type VideoRow = {
  videoId: string;
  familyId?: string;
  title?: string;
  status?: string;
  createdAt?: string;
  segments: number;
  stitched: boolean;
  reuse?: { segments?: number; reused?: number };
  moderation?: { status?: string; flags?: string[] };
};

/** Note the missing `family` parameter: `/admin/videos` is still a capped scan
 *  with only `limit`, so a family filter has to happen in the browser and the
 *  screen has to say so. */
export const listVideos = (params: { limit?: number } = {}) =>
  request<unknown>('/admin/videos' + qs(params)).then((body) =>
    listing<VideoRow>(body, 'videos'),
  );

/* ---------------------------------------------------------------- the money */

export type LedgerRow = {
  familyId?: string;
  entryId?: string;
  type?: string;
  credits: number;
  at?: string;
  reason?: string;
  actor?: string;
  commissionId?: string;
  waived: boolean;
  wouldHaveCost?: number | null;
};

export type LedgerTotals = {
  granted: number;
  held: number;
  released: number;
  outstanding: number;
  waivedCostUsd: number;
  entries: number;
};

/** Also scan-only and also without a family parameter. Capped at 500. */
export const getLedger = (params: { limit?: number } = {}) =>
  request<{ entries: LedgerRow[]; totals: LedgerTotals }>('/admin/ledger' + qs(params));

export type Stats = {
  families: number;
  unlimitedFamilies: number;
  assets: number;
  assetsEmbedded: number;
  assetsMissingEmbedding: number;
  aliases: number;
  videos: number;
  videosStitched: number;
  generationSpendUsd: number;
  segmentsTotal: number;
  segmentsReused: number;
  reuseRate: number;
};

export const getStats = () => request<Stats>('/admin/stats');

export const backfillEmbeddings = () =>
  request<{ embedded: number; remaining: number; failed: string[] }>(
    '/admin/maintenance/embeddings',
    { method: 'POST' },
  );

/* ------------------------------------------------------------------ verdicts */

/**
 * One cached safety decision.
 *
 * Two properties of the table shape the screen. It is a CACHE, not an audit log
 * — TTL is enabled, 7 days for allows and 30 for denies — so "the gate has never
 * refused X" is not a claim it can support. And its partition key is
 * `<policyVersion>#<the parent's own words>`, verified in production to contain
 * a child's first name and a home address, because parents type what they mean.
 *
 * So the text arrives as a stable `textFingerprint` and a length. `?reveal=1` is
 * a separate deliberate act that logs the operator's name server-side. `reason`
 * and `suggestion` are never redacted — they are the decision, and a safety
 * trail without them audits nothing — but they can quote the input back.
 */
export type VerdictRow = {
  policyVersion?: string;
  textFingerprint?: string;
  textLength?: number;
  /** Only present when the request asked to reveal, which is logged. */
  text?: string;
  allowed?: boolean;
  verdict?: string;
  reason?: string;
  suggestion?: string;
  at?: string;
  ttlAt?: string;
  expired?: boolean;
};

export const listVerdicts = (params: {
  allowed?: string;
  reveal?: string;
  next?: string;
  limit?: number;
}) =>
  request<unknown>('/admin/verdicts' + qs(params)).then((body) => {
    const meta = (body ?? {}) as Record<string, unknown>;
    return {
      ...listing<VerdictRow>(body, 'verdicts'),
      redacted: meta.redacted !== false,
      redactionNote: typeof meta.redactionNote === 'string' ? meta.redactionNote : undefined,
    };
  });

/* ------------------------------------------------------- devices and pairings */

export type DeviceRow = {
  deviceId: string;
  familyId?: string;
  familyName?: string;
  deviceName?: string;
  /** True means this device reaches /watch/* only — no spending, no approval.
   *  The difference between a child's iPad and a parent's. */
  childOnly?: boolean;
  pairedAt?: string;
  createdAt?: string;
  lastSeenAt?: string;
  /** Whether a bearer token exists. Never the token itself, in any mode. */
  hasToken?: boolean;
};

export const listDevices = (params: { family?: string; next?: string; limit?: number }) =>
  request<unknown>('/admin/devices' + qs(params)).then((body) =>
    listing<DeviceRow>(body, 'devices'),
  );

export type PairingRow = {
  /** Masked by the API. A pairing code is a bearer credential — the claim route
   *  is deliberately open because the claiming device has nothing else — so a
   *  staff page showing live codes is a page that can join a device to somebody
   *  else's household. */
  code?: string;
  familyId?: string;
  familyName?: string;
  childOnly?: boolean;
  createdAt?: string;
  expiresAt?: string;
  expired?: boolean;
};

export const listPairings = (params: { family?: string; next?: string; limit?: number }) =>
  request<unknown>('/admin/pairings' + qs(params)).then((body) =>
    listing<PairingRow>(body, 'pairings'),
  );

/* -------------------------------------------------------------------- config */

export type ConfigRow = {
  configKey?: string;
  note?: string;
  value?: Record<string, unknown>;
  /** Every assetId is dereferenced by the handler, so "pinned but the asset is
   *  gone" shows as `found: false` rather than as a value that merely looks set.
   *  That is the entire point of the screen: a reader that assumed the partition
   *  key was `key` rather than `configKey` was broken for weeks with the
   *  ValidationException swallowed, and a screen showing only the row would have
   *  shown a healthy pin the whole time. */
  resolves?: {
    assetId?: string;
    found?: boolean;
    canonical?: string;
    kind?: string;
    storageKey?: string;
    reuse?: string;
  };
};

export const getConfig = () =>
  request<unknown>('/admin/config').then((body) => ({
    ...listing<ConfigRow>(body, 'config'),
    partitionKey:
      typeof (body as Record<string, unknown>)?.partitionKey === 'string'
        ? ((body as Record<string, unknown>).partitionKey as string)
        : undefined,
    writable: (body as Record<string, unknown>)?.writable === true,
  }));

/* ------------------------------------------- pipeline / step functions ------ */

/** The polled endpoint. `running` is one `list_executions(RUNNING)` and nothing
 *  else; `commissions` costs a bounded scan and is the only way to see peeks. */
export type PipelineSummary = {
  stateMachineArn?: string | null;
  /** null, never 0, when Step Functions could not be reached. Reporting "0
   *  running" for a failed call is how an operator concludes the pipeline is
   *  fine while it is on fire. */
  running?: number | null;
  runningCommissionIds?: string[];
  executionsError?: string | null;
  commissions?: {
    generating?: number;
    previewing?: number;
    creditsHeld?: number;
    scannedCount?: number;
    truncated?: boolean;
  } | null;
  detail?: string;
};

export const getPipeline = (params: { source?: 'executions' } = {}) =>
  request<PipelineSummary>('/admin/pipeline' + qs(params));

export type ExecutionRow = {
  executionArn?: string;
  /** The name IS the commissionId — `commissions.py` passes
   *  `name=commission_id` to StartExecution, and the API returns it under both
   *  keys so nobody has to know that to read the code. Execution *tagging* does
   *  not exist as an API; this join is what replaces it, and it survives the
   *  state-machine rebuild that a tag would not. */
  name?: string;
  commissionId?: string;
  status?: string;
  startDate?: string;
  stopDate?: string;
  durationSeconds?: number;
  /** Non-zero means somebody redrove this by hand, out of band. Nothing in this
   *  repo calls RedriveExecution — a redrive regenerates every shot with no
   *  hold taken, i.e. unbilled spend. */
  redriveCount?: number;
  join?: 'commission' | 'no-commission-row';
  joinDetail?: string;
  familyId?: string | null;
  familyName?: string | null;
  familyExists?: boolean;
  request?: string | null;
  kind?: string;
  characterId?: string;
  /** BOTH status words are returned and neither is derived from the other. They
   *  disagree usefully: every real pipeline failure here ends as a SUCCEEDED
   *  execution, because the catcher ran and returned cleanly, while the
   *  commission says `failed`. Filtering executions on FAILED finds none. */
  commissionStatus?: string;
  creditsHeld?: number | null;
  videoId?: string;
  failureReason?: string;
};

export const listExecutions = (params: {
  status?: string;
  familyId?: string;
  limit?: number;
}) =>
  request<{
    executions: ExecutionRow[];
    count: number;
    truncated?: boolean;
    limit?: number;
    statusFilter?: string | null;
    familyFilterAppliedLocally?: boolean;
  }>('/admin/pipeline/executions' + qs(params));

export type Failure = {
  /** "execution" — the execution itself failed. "caught" — the catch-all
   *  handler ran, so Step Functions calls it SUCCEEDED and the commission still
   *  failed. "history" — recovered from the reverse event tail. */
  source?: string;
  event?: string;
  error?: string;
  cause?: string;
  state?: string | null;
  at?: string;
  release?: unknown;
  detail?: string;
};

export type Trail = {
  /** Newest first and deduplicated, so element 0 is where a running execution
   *  is right now. */
  states?: string[];
  currentState?: string | null;
  failures?: Failure[];
  eventsRead?: number;
  /** A `nextToken` on a REVERSE read means there is OLDER history before this
   *  window — so a failure early in a long run can be off the end of the tail. */
  truncated?: boolean;
  detail?: string;
  error?: string;
};

export type PipelineCommission = {
  commissionId?: string;
  familyId?: string;
  status?: string;
  kind?: string;
  request?: string;
  characterId?: string;
  createdAt?: string;
  creditsHeld?: number | null;
  creditsWaived?: number | null;
  animaticCostUsd?: number | null;
  failureReason?: string;
  failedAt?: string;
  videoId?: string;
  video?: {
    videoId?: string;
    title?: string;
    status?: string;
    segments?: number;
    stitched?: boolean;
    stitch?: Record<string, unknown>;
  } | null;
  shotsPlanned?: number;
  shotsByStatus?: Record<string, number>;
  /** Always null today, with a reason attached. `showling-shots.status` is
   *  written once by plan.py as "planned" and nothing advances it, so a progress
   *  bar here would be a lie. */
  shotsCompleted?: number | null;
  shotProgressDetail?: string;
};

export type ExecutionDetail = {
  executionArn?: string;
  name?: string;
  commissionId?: string;
  status?: string;
  startDate?: string;
  stopDate?: string;
  durationSeconds?: number;
  redriveCount?: number;
  /** What the pipeline was asked for, straight from the execution. For an
   *  orphaned execution this is the ONLY surviving record of whose it was. */
  input?: Record<string, unknown> | null;
  failure?: Failure | null;
  commission?: PipelineCommission | null;
  join?: 'commission' | 'no-commission-row';
  trail?: Trail;
  /** Present on the 404 body when the execution is gone: aged past the 90-day
   *  STANDARD retention, or lost to a state-machine rebuild. */
  detail?: string;
};

export const getExecution = (name: string, params: { historyEvents?: number } = {}) =>
  request<ExecutionDetail>(
    `/admin/pipeline/executions/${encodeURIComponent(name)}` + qs(params),
  );

/**
 * The rows where the two sources of truth disagree, or time has run out.
 *
 * Each finding carries the rule that flagged it in `why`, so nothing here is a
 * heuristic dressed up as a fact. `no-execution` is the expensive one: the hold
 * and its ledger row are written before `start_execution` is called, so a
 * `generating` commission with no execution means a family was charged for
 * something that never started.
 */
export type StuckRow = {
  kind: string;
  commissionId?: string;
  familyId?: string | null;
  request?: string | null;
  commissionStatus?: string | null;
  commissionKind?: string;
  createdAt?: string;
  executionStatus?: string | null;
  executionArn?: string;
  startDate?: string;
  /** Seconds since the work started — "how long ago", never "how long it ran".
   *  Only `orphan-execution` rows carry `durationSeconds` as well, because they
   *  are the only kind whose execution has already finished. */
  ageSeconds?: number | null;
  durationSeconds?: number | null;
  creditsHeld?: number | null;
  /** The diagnosis: what the server observed. */
  why?: string;
  /** The instruction: what a human should now do about it, including "nothing".
   *  Required of every finding server-side — see `_finding` in
   *  admin_pipeline.py for why a diagnosis alone is not actionable. */
  do?: string;
};

export const getStuck = (params: { runningLongerThanSeconds?: number } = {}) =>
  request<{
    stuck: StuckRow[];
    count: number;
    byKind?: Record<string, number>;
    thresholds?: Record<string, number>;
    executionsError?: string | null;
    scanned?: {
      commissions?: number;
      truncated?: boolean;
      executions?: number;
      executionsTruncated?: boolean;
    };
  }>('/admin/pipeline/stuck' + qs(params));

/**
 * The sweep: which families are holding credits for a film nothing is making.
 *
 * THE ROUTE THE CONSOLE WAS TOLD TO USE AND COULD NOT REACH. All four
 * /admin/pipeline routes refuse on this deployment, and every one of those
 * refusals carries the same `alternative` — "GET /admin/pipeline/sweep answers
 * the question that matters". That sentence is printed verbatim on the
 * Overview, the Pipeline screen, the Executions screen and every commission
 * page, four places telling an operator to go somewhere this client had no
 * function for. The answer was deployed the whole time: it is registered in
 * azure/functions/orchestrator.py rather than function_app.py's ROUTES table,
 * which is why a reader checking that table concludes it does not exist.
 *
 * GET IS A DRY RUN AND THERE IS DELIBERATELY NO POST HERE. orchestrator.py:
 * "GET reports; POST refunds… A refund must not be something a browser can do
 * by loading a URL." Every finding still says what WOULD happen in `release`,
 * and the timer behind the same code runs with apply=True every fifteen
 * minutes — so reading this costs nothing and changes nothing, and a hold this
 * page shows as `would-release` is one the unattended sweep will return on its
 * own. Adding the POST is a separate decision about a control that moves money
 * from a console with no MFA story; it is not smuggled in with the read.
 */
export type SweepFinding = {
  /** `no-orchestration` and `orchestration-dead` are the two that owe money.
   *  `finished-but-open`, `still-running`, `peek-stalled` and
   *  `status-unavailable` explicitly do not — see `_judge` in sweeper.py. */
  kind: string;
  commissionId?: string;
  familyId?: string | null;
  commissionStatus?: string | null;
  commissionKind?: string;
  request?: string | null;
  createdAt?: string | null;
  ageSeconds?: number | null;
  creditsHeld?: number | null;
  videoId?: string | null;
  orchestrationStatus?: string | null;
  /** The diagnosis: what the sweeper observed. */
  why?: string;
  /** The instruction, including "nothing". Required of every finding. */
  do?: string;
  /** On a GET: what a POST would do. "would-release" is the only value that
   *  moves credits; everything else is a decision already made. */
  release?: string;
};

export type Sweep = {
  findings: SweepFinding[];
  count: number;
  /** Always 0 on a GET, and worth rendering as such: an operator who cannot
   *  tell a dry run from a real one reads "would-release" as "released". */
  released: number;
  applied: boolean;
  byKind?: Record<string, number>;
  open?: number;
  candidates?: number;
  scanned?: number;
  truncated?: boolean;
  thresholdSeconds?: number;
  /** Commissions whose orchestration status could NOT be read. Never refunded
   *  on a failed query — that is the difference between a sweep and an outage
   *  refunding the whole table. */
  statusErrors?: number;
  detail?: string;
};

export const getSweep = (params: { olderThanSeconds?: number } = {}) =>
  request<Sweep>('/admin/pipeline/sweep' + qs(params));

/* -------------------------------------------------------------------- health */

/** `/admin/health` is an echo — it proves the staff gateway, the staff pool and
 *  this token line up, and nothing more. There is deliberately no table-health
 *  endpoint yet; the Health screen builds what it can from the browsers and says
 *  plainly which checks are missing. */
export const getHealth = () =>
  request<{ ok?: boolean; surface?: string; staff?: string }>('/admin/health');

export { numberOr as asNumber };
